import { SystemSettingsPageStore } from './system-settings-page.store';
import { Component, computed, effect, inject, OnDestroy, TemplateRef, viewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { LayoutService } from '../../../modules/layout/services/layout.service';
import { Ripple } from '@openng/optimus-ui/ripple';
import { TranslatePipe } from '@ngx-translate/core';
import { toKebabCase } from '../../../utils/string.utils';
import { OrganizationSettingTypeEnum } from '@libs/types/src/organization/enum/organization-setting-type.enum';
import { featureOn } from '../../../feature-flag';
import { SystemSettingsMenuItem } from './system-settings-page.types';

@Component({
    selector: 'app-system-settings-page',
    templateUrl: './system-settings-page.component.html',
    styleUrl: './system-settings-page.component.scss',
    providers: [SystemSettingsPageStore],
    imports: [
        RouterOutlet,
        NgTemplateOutlet,
        Ripple,
        TranslatePipe,
        NgClass,
    ],
})
export class SystemSettingsPageComponent implements OnDestroy {

    protected readonly store = inject(SystemSettingsPageStore);
    protected readonly route = inject(ActivatedRoute);
    protected readonly router = inject(Router);
    protected readonly layoutService = inject(LayoutService);

    protected readonly url = toSignal(this.router.events);

    protected subsubmenuContent = viewChild<TemplateRef<unknown> | null>('subsubmenuContent');

    protected items = computed<SystemSettingsMenuItem[]>(() => {
        const items: SystemSettingsMenuItem[] = [];
        const item = (key: string): SystemSettingsMenuItem => ({
            key,
            path: toKebabCase(key),
            label: `system.settings.${key}.labelMulti`,
        });

        if (this.store.isAllowedGlobal('service.read')() && this.store.globalGlobalStore.organizationStore.isSettingActive(OrganizationSettingTypeEnum.HEALTHCARE_SOCIAL_SETTINGS)()) {
            items.push(item('serviceType'));
        }

        if (this.store.isAllowedGlobal('diagnosisBonification.read')() && featureOn.PURO) {
            items.push(item('diagnosis'));
        }

        if (featureOn.PURO) {
            items.push(item('dataInterfaceVersion'));
        }

        if (this.store.isAllowedGlobal('systemPuro.update')() && featureOn.PURO) {
            items.push(item('puro'));
            items.push(item('pmup'));
        }

        if (featureOn.GUIDE && this.store.isAllowedGlobal('systemGuide.create')()) {
            items.push(item('guide'));
        }
        if (featureOn.USER_MESSAGES) {
            if (this.store.isAllowedGlobal('userMessage.read')()) {
                items.push(item('userMessages'));
            }

            if (this.store.isAllowedGlobal('methodicalMessage.read')()) {
                items.push(item('methodicalMessages'));
            }
        }

        if (this.store.isAllowedGlobal('systemEnum.read')()) {
            items.push(item('doctorSpecialization'));
        }

        if (this.store.isAllowedGlobal('system.read')()) {
            items.push(item('backgroundTasks'));
            items.push(item('statistics'));
            if (featureOn.TRASH) items.push(item('trash'));
        }

        if (this.store.isAllowedGlobal('organizationBilling.read')()) {
            items.push(item('platformBilling'));
        }

        return items;
    });

    protected readonly activePath = computed(() => {
        this.url();
        const url = this.router.url.split(/[?#]/, 1)[0];
        if (!url) return '';

        const lookFor = '/content/system/';
        const index = url.indexOf(lookFor);

        return index < 0 ? '' : url.substring(index + lookFor.length).replace(/^\/+|\/+$/g, '');
    });

    constructor() {
        effect(() => {
            const menu = this.subsubmenuContent();
            if (!menu) return;
            this.layoutService.setSubmenuContent(menu);
        });
    }

    public ngOnDestroy(): void {
        this.layoutService.setSubmenuContent(null);
    }

    protected isItemActive(item: SystemSettingsMenuItem): boolean {
        return this.activePath() === item.path || this.activePath().startsWith(`${item.path}/`);
    }

    protected async itemOnClick(path: string): Promise<void> {
        await this.router.navigateByUrl(
            `content/system/${path}`,
        );
    }

}
